/**
 * Public Scorecard — TurfPulse troll-kryptonite page.
 *
 * Read-only, no auth. Pulls from /api/scorecard/public + /recent-decisions.
 * Designed to silence skeptics with big numbers, transparent assumptions,
 * and a live ticker of real engine decisions.
 *
 * Marketing positioning: "Every number you see refreshes every 15 minutes
 * from our production database. The math is in plain sight."
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Droplet, TrendingDown, Activity, Brain, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';

const STATUS_BADGE = {
  ok:            { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300', label: 'FIRE' },
  fired:         { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300', label: 'FIRE' },
  fired_round:   { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300', label: 'FIRE' },
  ok_with_soak:  { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300', label: 'FIRE+SOAK' },
  manual_run_now:{ color: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300', label: 'MANUAL' },
  skipped:       { color: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300', label: 'SKIP' },
  rain_skip:     { color: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300', label: 'RAIN SKIP' },
  tier_blocked:  { color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', label: 'TIER GATE' },
  per_zone_skip: { color: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300', label: 'ZONE SKIP' },
  ghost:         { color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', label: 'GHOST' },
  advisory_no_sensors: { color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', label: 'ADVISORY' },
  error:         { color: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300', label: 'ERROR' },
  round_error:   { color: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300', label: 'ROUND ERROR' },
};

const fmtNumber = (n) => (n ?? 0).toLocaleString('en-US');
const fmtDollar = (n) => '$' + (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const Stat = ({ icon: Icon, label, value, sub, accent = 'emerald' }) => (
  <Card className="border-2" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
        <Icon size={14} className={`text-${accent}-600 dark:text-${accent}-400`} />
        {label}
      </div>
      <div className={`text-4xl sm:text-5xl font-bold text-${accent}-700 dark:text-${accent}-300 mt-2 leading-none`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </CardContent>
  </Card>
);

const ScorecardPage = () => {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r] = await Promise.all([
          axios.get(`${API_URL}/api/scorecard/public`),
          axios.get(`${API_URL}/api/scorecard/recent-decisions`),
        ]);
        setStats(s.data);
        setRecent(r.data);
      } catch (e) {
        setError(e?.message || 'Could not load scorecard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="scorecard-page">
      <SEO
        title="The TurfPulse Scorecard · Real Engine, Real Receipts"
        description="Live stats from the TurfPulse Decision Engine. Skip rate, gallons saved, and every recent engine call — refreshed every 15 minutes from production data."
      />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 py-10 sm:py-16">
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-bold mb-3">
          ⌬ The Scorecard
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
          Every engine call.<br />
          <span className="text-emerald-700 dark:text-emerald-400">Out in the open.</span>
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
          TurfPulse's Decision Engine runs against real Ecowitt sensors + Open-Meteo forecasts on real
          customer yards. This page is wired straight to production. No marketing math, no hand-waving —
          if you doubt it, refresh it.
        </p>
        {stats && (
          <div className="text-xs text-muted-foreground mt-4 flex items-center gap-3" data-testid="scorecard-as-of">
            <Clock size={12} />
            <span>Last refreshed: {fmtTime(stats.as_of)}</span>
            <span>·</span>
            <Badge variant="outline" className="text-[10px]">Engine v{stats.engine_version}</Badge>
            <span>·</span>
            <span>Cache: 15 min</span>
          </div>
        )}
      </section>

      {loading && (
        <div className="max-w-5xl mx-auto px-4 py-12 text-center text-muted-foreground" data-testid="scorecard-loading">
          Loading live stats…
        </div>
      )}

      {error && (
        <div className="max-w-5xl mx-auto px-4" data-testid="scorecard-error">
          <Card className="border-rose-300 bg-rose-50/40 dark:bg-rose-950/20">
            <CardContent className="p-6 text-rose-900 dark:text-rose-200">
              Couldn't load stats: {error}
            </CardContent>
          </Card>
        </div>
      )}

      {stats && (
        <>
          {/* Headline stats — the receipts */}
          <section className="max-w-5xl mx-auto px-4 pb-8">
            <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-3">All-time receipts</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat
                icon={Brain}
                label="Engine decisions"
                value={fmtNumber(stats.totals.decisions_all_time)}
                sub={`${fmtNumber(stats.totals.skips_all_time)} skips · ${fmtNumber(stats.totals.fires_all_time)} fires`}
                accent="emerald"
              />
              <Stat
                icon={TrendingDown}
                label="Skip rate"
                value={`${stats.totals.skip_rate_pct}%`}
                sub="When the engine says don't water"
                accent="emerald"
              />
              <Stat
                icon={Droplet}
                label="Gallons saved"
                value={fmtNumber(stats.totals.gallons_saved)}
                sub="Conservative — see math below"
                accent="sky"
              />
              <Stat
                icon={CheckCircle2}
                label="Dollars saved"
                value={fmtDollar(stats.totals.dollars_saved)}
                sub="At $5 per 1,000 gal (TX avg)"
                accent="emerald"
              />
            </div>
          </section>

          {/* 30-day window */}
          <section className="max-w-5xl mx-auto px-4 pb-8">
            <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-3">Last 30 days</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat icon={Brain} label="Decisions" value={fmtNumber(stats.last_30_days.decisions)} accent="emerald" />
              <Stat icon={TrendingDown} label="Skip rate" value={`${stats.last_30_days.skip_rate_pct}%`} accent="emerald" />
              <Stat icon={Activity} label="Active yards" value={fmtNumber(stats.last_30_days.active_accounts)} sub="Running auto-fires" accent="sky" />
              <Stat icon={Droplet} label="Gal saved" value={fmtNumber(stats.last_30_days.gallons_saved)} sub={fmtDollar(stats.last_30_days.dollars_saved)} accent="sky" />
            </div>
          </section>

          {/* Sensor footprint */}
          <section className="max-w-5xl mx-auto px-4 pb-8">
            <Card>
              <CardContent className="p-6">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Sensor footprint (last 7 days)</div>
                <div className="flex flex-wrap gap-6 text-sm">
                  <div>
                    <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{fmtNumber(stats.last_7_days.sensor_accounts_reporting)}</span>
                    <span className="text-muted-foreground ml-2">yards reporting</span>
                  </div>
                  <div>
                    <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{fmtNumber(stats.last_7_days.sensor_readings_captured)}</span>
                    <span className="text-muted-foreground ml-2">Ecowitt snapshots captured</span>
                  </div>
                  <div>
                    <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{fmtNumber(stats.footprint.users_with_ec_sensors)}</span>
                    <span className="text-muted-foreground ml-2">yards with EC tracking (WH52)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Recent engine decisions — the live ticker */}
          <section className="max-w-5xl mx-auto px-4 pb-8">
            <h2 className="text-base font-bold mb-3">Recent engine calls</h2>
            <p className="text-xs text-muted-foreground mb-4">Anonymized. No user IDs, no zone labels. Just the verdict + reason.</p>
            <Card>
              <CardContent className="p-0">
                <div className="divide-y" data-testid="recent-decisions-list">
                  {(recent?.decisions || []).slice(0, 25).map((d, i) => {
                    const badge = STATUS_BADGE[d.status] || { color: 'bg-slate-100 text-slate-700', label: d.status?.toUpperCase() };
                    return (
                      <div key={i} className="p-3 flex items-start gap-3 text-sm" data-testid={`recent-decision-${i}`}>
                        <Badge className={`${badge.color} text-[10px] font-bold shrink-0 mt-0.5`} variant="outline">{badge.label}</Badge>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">
                            {fmtTime(d.fired_at)} · {d.zone_count} zone{d.zone_count !== 1 ? 's' : ''}
                          </div>
                          {d.reason && <div className="text-xs italic text-muted-foreground mt-0.5 line-clamp-2">{d.reason}</div>}
                        </div>
                      </div>
                    );
                  })}
                  {(!recent?.decisions || recent.decisions.length === 0) && (
                    <div className="p-6 text-center text-sm text-muted-foreground italic" data-testid="no-decisions">
                      No engine decisions logged yet — first auto-fires write here in real-time.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Assumptions — the troll-killer */}
          <section className="max-w-5xl mx-auto px-4 pb-12">
            <Card className="border-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-950/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-emerald-700 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-base mb-2">Show the math</h3>
                    <p className="text-sm text-muted-foreground mb-3">{stats.assumptions.method}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                      <div className="bg-white dark:bg-slate-900 rounded p-2 border">
                        <div className="text-muted-foreground">Gallons / skip</div>
                        <div className="text-base font-bold">{stats.assumptions.gallons_per_skip} gal</div>
                      </div>
                      <div className="bg-white dark:bg-slate-900 rounded p-2 border">
                        <div className="text-muted-foreground">$ / 1,000 gal</div>
                        <div className="text-base font-bold">${stats.assumptions.dollar_rate_per_1000_gal.toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground italic mt-3">
                      Every number on this page is calculated server-side from the production database. We round DOWN at every step — your actual savings are likely higher than what we show. If you spot an error, email founder@turfpulse.app.
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* CTA */}
          <section className="max-w-5xl mx-auto px-4 pb-16">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-3">Want your yard's data on this page?</p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
                data-testid="scorecard-cta-pricing"
              >
                See pricing → join the next 50 yards
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default ScorecardPage;
